/**
 * PGS Recovery Screen (Phase 2)
 *
 * Shown when PGS cloud save exists but user has no active Supabase session.
 * Guides user to sign in with their original auth provider to restore progress.
 */

import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
} from 'react-native';

import { useLanguageStore } from '../../features/quiz-core/languageStore';
import { t } from '../../i18n/translations';
import type { PGSCloudSavePayload } from '../types/playGames';

interface PGSRecoveryScreenProps {
  cloudSave: PGSCloudSavePayload;
  onSignIn: () => void;
  onSkip: () => void;
}

const PROVIDER_LABELS: Record<string, { label: string; icon: string }> = {
  google: { label: 'Google', icon: 'logo-google' },
  apple: { label: 'Apple', icon: 'logo-apple' },
  email: { label: 'Email', icon: 'mail' },
};

export default function PGSRecoveryScreen({
  cloudSave,
  onSignIn,
  onSkip,
}: PGSRecoveryScreenProps) {
  const { language } = useLanguageStore();
  if (Platform.OS !== 'android') return null;

  const provider = PROVIDER_LABELS[cloudSave.authProvider] ?? {
    label: 'your account',
    icon: 'person',
  };

  return (
    <LinearGradient
      colors={['#3B4CCA', '#4C63D2', '#5A73DA']}
      style={styles.container}
    >
      <View style={styles.card}>
        <View style={styles.iconCircle}>
          <Ionicons name="cloud-download" size={48} color="#3B4CCA" />
        </View>

        <Text style={styles.title}>{t('recovery_welcome_back', language) || 'Welcome Back'}</Text>
        <Text style={styles.subtitle}>
          {t('recovery_saved_progress_found', language) || 'We found your saved progress from a previous session.'}
        </Text>

        <View style={styles.statsRow}>
          <StatBadge label={t('profile.level', language) || 'Journey'} value={String(cloudSave.currentLevel)} />
          <StatBadge label="XP" value={String(cloudSave.totalXP)} />
          <StatBadge label={t('profile.streak', language) || 'Days'} value={`${cloudSave.longestStreak}d`} />
        </View>

        <Text style={styles.hint}>
          {(t('recovery_sign_in_restore', language) || 'Sign in with {{provider}} to restore your progress.').replace('{{provider}}', provider.label)}
        </Text>

        <TouchableOpacity style={styles.signInButton} onPress={onSignIn}>
          <Ionicons
            name={provider.icon as keyof typeof Ionicons.glyphMap}
            size={20}
            color="white"
            style={{ marginRight: 8 }}
          />
          <Text style={styles.signInText}>
            {(t('recovery_sign_in_restore_cta', language) || 'Sign in with {{provider}}').replace('{{provider}}', provider.label)}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.skipButton} onPress={onSkip}>
          <Text style={styles.skipText}>{t('recovery_skip_restore', language) || 'Keep Playing Without Restore'}</Text>
        </TouchableOpacity>
      </View>
    </LinearGradient>
  );
}

function StatBadge({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.statBadge}>
      <Text style={styles.statValue}>{value}</Text>
      <Text style={styles.statLabel}>{label}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  card: {
    backgroundColor: 'white',
    borderRadius: 24,
    padding: 32,
    width: '100%',
    maxWidth: 400,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 24,
    elevation: 12,
  },
  iconCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#EEF2FF',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: '#1F2937',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 15,
    color: '#6B7280',
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 20,
  },
  statsRow: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 24,
  },
  statBadge: {
    backgroundColor: '#F3F4F6',
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 20,
    alignItems: 'center',
    minWidth: 72,
  },
  statValue: {
    fontSize: 18,
    fontWeight: '700',
    color: '#3B4CCA',
  },
  statLabel: {
    fontSize: 12,
    color: '#9CA3AF',
    marginTop: 2,
  },
  hint: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: 20,
  },
  signInButton: {
    flexDirection: 'row',
    backgroundColor: '#3B4CCA',
    borderRadius: 14,
    paddingVertical: 14,
    paddingHorizontal: 32,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    marginBottom: 12,
  },
  signInText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  skipButton: {
    paddingVertical: 12,
  },
  skipText: {
    color: '#9CA3AF',
    fontSize: 14,
  },
});
